/**
 * PEAR Review Form Model
 * Performance Evaluation Assessment Review
 */

export interface PEARScoreItem {
  criterion: string;
  score: number;
  comments?: string;
}

export interface PEARHighPerformanceItem {
  attributeLeft: string;
  scoreLeft: number;
  attributeRight: string;
  scoreRight: number;
}

// ============================================
// PART 2: Quarterly 1:1 Review Types
// ============================================

// Section A: Performance Key Factor - each sub-item gets its own rating
export interface PEARKeyFactorSubItem {
  description: string;
  selfRating: string; // A+, A, A-, B+, B, C+, C, C-, D, E, F
  managerRating: string;
}

export interface PEARKeyFactorCategory {
  category: string;
  subItems: PEARKeyFactorSubItem[];
}

export interface PEARKPIItem {
  description: string;
  measurement: string;
}

export interface PEAROKRKeyResult {
  description: string;
  selfRating: number; // 1-5
  managerPerformanceRating: number; // 1-5
  managerAlignmentRating: number; // 1-5
  managerComment: string;
}

export interface PEARCoreValueItem {
  value: string;
  acronym: string; // Single letter (A, C, T, R, E, F)
  description: string;
  selfRating: string; // A+, A, A-, B, C, D, F
  managerRating: string;
  group: 'ACT' | 'REF';
}

export interface PEARReviewFormType {
  reviewPeriod: string;
  employeeId?: string;
  employeeName?: string;
  designation?: string;
  department?: string;
  reportsTo?: string;
  joiningDate?: string;
  evaluationPurpose?: string;
  
  // Part 1: Functional Skills
  qualityOfWork: PEARScoreItem[];
  workHabits: PEARScoreItem[];
  jobKnowledge: PEARScoreItem[];
  highPerformanceCulture: PEARHighPerformanceItem[];
  
  // Part 1: Interpersonal Skills  
  interpersonalSkills: PEARScoreItem[];
  leadershipProblemSolving: PEARScoreItem[];

  // ============================================
  // PART 2: Quarterly 1:1 Review Fields
  // ============================================

  // Section A: Performance Key Factors (9 categories with sub-items)
  performanceKeyFactors: PEARKeyFactorCategory[];
  performanceKeyFactorsManagerComments?: string;

  // Section B: BSC Reference
  bscScore?: number;
  bscQuarter?: string; // Track which quarter the BSC is from
  bscManagerComments?: string;

  // Section C: KPI Setting for Next Quarter
  nextQuarterKPIs: PEARKPIItem[];

  // Section D: OKRs
  okrObjective?: string;
  okrObjectiveChallenge?: number; // 1-5
  okrKeyResults: PEAROKRKeyResult[];

  // Section E: CIS (Continuous Improvement)
  cisImprovements: string[];
  cisManagerComments?: string;

  // Section F: 360° Review Feedback
  feedback360?: string;

  // Section G: Core Values
  coreValues: PEARCoreValueItem[];

  // Section H: Personal Development
  strengths: string[];
  developmentNeeds: string[];
  managerDevelopmentSupport?: string;

  // Final Section
  overallManagerRating?: number; // 1-10
  overallManagerComments?: string;
}

const pearReviewModel = {
  formId: 'pearReviewForm',
  formField: {
    reviewPeriod: {
      name: 'reviewPeriod',
      label: 'Review Period',
      required: true,
      requiredErrorMsg: 'Review period is required',
    },
    employeeName: {
      name: 'employeeName',
      label: 'Name of Employee',
    },
    designation: {
      name: 'designation',
      label: 'Designation',
    },
    department: {
      name: 'department',
      label: 'Department',
    },
    reportsTo: {
      name: 'reportsTo',
      label: 'Reports To',
    },
    joiningDate: {
      name: 'joiningDate',
      label: 'Joining Date',
    },
    evaluationPurpose: {
      name: 'evaluationPurpose',
      label: 'Evaluation Purpose',
    },
    qualityOfWork: {
      name: 'qualityOfWork',
      label: 'Quality of Work',
    },
    workHabits: {
      name: 'workHabits',
      label: 'Work Habits',
    },
    jobKnowledge: {
      name: 'jobKnowledge',
      label: 'Job Knowledge, Duty & Skills',
    },
    highPerformanceCulture: {
      name: 'highPerformanceCulture',
      label: 'High Performance Culture',
    },
    interpersonalSkills: {
      name: 'interpersonalSkills',
      label: 'Interpersonal Skills/Relations/Behaviour',
    },
    leadershipProblemSolving: {
      name: 'leadershipProblemSolving',
      label: 'Leadership/Problem-Solving',
    },
    // Part 2 Fields
    performanceKeyFactors: {
      name: 'performanceKeyFactors',
      label: 'Performance Key Factors',
    },
    performanceKeyFactorsManagerComments: {
      name: 'performanceKeyFactorsManagerComments',
      label: 'Manager Comments',
    },
    bscScore: {
      name: 'bscScore',
      label: 'BSC Score (%)',
    },
    bscManagerComments: {
      name: 'bscManagerComments',
      label: 'BSC Manager Comments',
    },
    nextQuarterKPIs: {
      name: 'nextQuarterKPIs',
      label: 'Next Quarter KPIs',
    },
    okrObjective: {
      name: 'okrObjective',
      label: 'OKR Objective',
    },
    okrObjectiveChallenge: {
      name: 'okrObjectiveChallenge',
      label: 'Objective Challenge (1-5)',
    },
    okrKeyResults: {
      name: 'okrKeyResults',
      label: 'OKR Key Results',
    },
    cisImprovements: {
      name: 'cisImprovements',
      label: 'CIS Improvements',
    },
    cisManagerComments: {
      name: 'cisManagerComments',
      label: 'CIS Manager Comments',
    },
    feedback360: {
      name: 'feedback360',
      label: '360° Review Feedback',
    },
    coreValues: {
      name: 'coreValues',
      label: 'Core Values',
    },
    strengths: {
      name: 'strengths',
      label: 'Strengths',
    },
    developmentNeeds: {
      name: 'developmentNeeds',
      label: 'Development Needs',
    },
    managerDevelopmentSupport: {
      name: 'managerDevelopmentSupport',
      label: 'Manager Development Support',
    },
    overallManagerRating: {
      name: 'overallManagerRating',
      label: 'Overall Manager Rating (1-10)',
    },
    overallManagerComments: {
      name: 'overallManagerComments',
      label: 'Overall Manager Comments',
    },
  },
  initialValues: {
    reviewPeriod: '',
    employeeName: '',
    designation: '',
    department: '',
    reportsTo: '',
    joiningDate: '',
    evaluationPurpose: '',
    qualityOfWork: [
      { criterion: 'Accuracy, neatness and timeliness of work.', score: 0 },
      { criterion: 'Adherence to duties and procedures in Job Description and Work Instructions.', score: 0 },
      { criterion: "Synchronization with organization's/functional goals.", score: 0 },
    ],
    workHabits: [
      { criterion: 'Punctuality to work', score: 0 },
      { criterion: 'Commitment', score: 0 },
      { criterion: 'Proactiveness, look for things to do, takes initiative at workplace.', score: 0 },
      { criterion: 'Submits reports on time and meets deadlines.', score: 0 },
    ],
    jobKnowledge: [
      { criterion: 'Skill and ability to perform job satisfactorily.', score: 0 },
      { criterion: 'Shown interest in learning and improving.', score: 0 },
      { criterion: 'Policy compliance.', score: 0 },
    ],
    highPerformanceCulture: [
      { attributeLeft: 'Works well under pressure', scoreLeft: 0, attributeRight: 'Has ability to endure', scoreRight: 0 },
      { attributeLeft: 'Meets deadlines', scoreLeft: 0, attributeRight: 'Gets the job finished on time', scoreRight: 0 },
      { attributeLeft: 'Achieve above average', scoreLeft: 0, attributeRight: 'Stays within Budget', scoreRight: 0 },
      { attributeLeft: 'Achieved highest possible goals', scoreLeft: 0, attributeRight: 'Is willing to stretch to the limit', scoreRight: 0 },
      { attributeLeft: 'Star Performer', scoreLeft: 0, attributeRight: 'Gets exceptional results', scoreRight: 0 },
    ],
    interpersonalSkills: [
      { criterion: 'Responds and contributes to team efforts.', score: 0 },
      { criterion: 'Responds positively to suggestions and instructions and criticism.', score: 0 },
      { criterion: 'Keeps supervisor informed of all details.', score: 0 },
      { criterion: 'Adapts well to changing circumstances.', score: 0 },
      { criterion: 'Seeks feedback to improve.', score: 0 },
      { criterion: 'Aligned with ACT and REF VALUES/CULTURE', score: 0 },
      { criterion: 'Work as a team', score: 0 },
    ],
    leadershipProblemSolving: [
      { criterion: 'Aspirant to climb up the ladder, accepts challenges, new responsibilities and roles.', score: 0 },
      { criterion: "Innovative thinking - contribution to organization's function(s) and personal growth.", score: 0 },
      { criterion: 'Work motivation.', score: 0 },
    ],

    // ============================================
    // PART 2: Quarterly 1:1 Review Defaults
    // ============================================

    // Section A: Performance Key Factors (9 categories, each sub-item is individually rated)
    performanceKeyFactors: [
      {
        category: 'Job Knowledge and Skills',
        subItems: [
          { description: 'Understands job requirements and responsibilities', selfRating: '', managerRating: '' },
          { description: 'Demonstrates an understanding of the work instructions, processes, equipment and materials required to perform the job', selfRating: '', managerRating: '' },
        ],
      },
      {
        category: 'Quality of Work',
        subItems: [
          { description: 'Shows concern for the goals and needs of the department', selfRating: '', managerRating: '' },
          { description: 'Completes assignments fully, accurately, and in a timely manner', selfRating: '', managerRating: '' },
          { description: 'Keeps track of progress for each assignment; meanwhile, uses work time productively', selfRating: '', managerRating: '' },
          { description: 'Finishes assigned task and volunteering to help in other areas as needed', selfRating: '', managerRating: '' },
        ],
      },
      {
        category: 'Policy Compliance',
        subItems: [
          { description: 'Follows existing and adapts to evolving company policies/procedures', selfRating: '', managerRating: '' },
          { description: 'Follows company policies and procedures for corrective action if needed', selfRating: '', managerRating: '' },
        ],
      },
      {
        category: 'Communication/Interpersonal Skills',
        subItems: [
          { description: 'Speak and write clearly; keeps clear written records (as needed) so that others can follow up', selfRating: '', managerRating: '' },
          { description: 'Displays effective listening skills, meaning takes into account and acts on suggestions that will help to complete tasks', selfRating: '', managerRating: '' },
          { description: 'Share information openly, honestly, thoroughly and in a timely manner to complete assignment', selfRating: '', managerRating: '' },
          { description: 'Effectively communicates with coworkers in other departments as needed to complete assignments', selfRating: '', managerRating: '' },
        ],
      },
      {
        category: 'Collaboration/Teamwork',
        subItems: [
          { description: 'Cooperate and work well in a team', selfRating: '', managerRating: '' },
          { description: 'Supports diversity by showing respect for all individuals; patient with each other if there is a language barrier', selfRating: '', managerRating: '' },
          { description: 'Uses tact to maintain a peaceful and productive work relationships with co-workers', selfRating: '', managerRating: '' },
          { description: 'Flexible to changing priorities and demands', selfRating: '', managerRating: '' },
          { description: 'Shares information and supplies with others to develop positive and cooperative work environment', selfRating: '', managerRating: '' },
        ],
      },
      {
        category: 'Problem Solving/Creativity',
        subItems: [
          { description: 'Able to identify and analyze problems and determine causes', selfRating: '', managerRating: '' },
          { description: 'Ready willing and able to create solutions', selfRating: '', managerRating: '' },
          { description: 'Able to evaluate alternative solutions and give constructive suggestions', selfRating: '', managerRating: '' },
          { description: 'Takes or recommends appropriate actions', selfRating: '', managerRating: '' },
          { description: 'Follows up to ensure problems are resolved, and adds notes where needed for record keeping', selfRating: '', managerRating: '' },
        ],
      },
      {
        category: 'Customer Focus',
        subItems: [
          { description: 'Acts with customers in mind; dedicated to meeting the expectations and requirements of internal depts and external customers', selfRating: '', managerRating: '' },
          { description: 'Establishes and maintains effective relationships with customers and gains their trust and respect', selfRating: '', managerRating: '' },
          { description: 'Goes above and beyond what is expected to anticipate customer needs and respond accordingly', selfRating: '', managerRating: '' },
        ],
      },
      {
        category: 'Attendance',
        subItems: [
          { description: 'Clocked in and working at indicated start time', selfRating: '', managerRating: '' },
          { description: 'Contacts immediate supervisor of any late arrival/absence before assigned work shift starts', selfRating: '', managerRating: '' },
          { description: 'Submits leave application form in advance for approval (unless emergency)', selfRating: '', managerRating: '' },
        ],
      },
      {
        category: 'Independent Work',
        subItems: [
          { description: 'Performs job responsibility with and without supervisor/manager present', selfRating: '', managerRating: '' },
          { description: 'Exhibits reliability in being available for work, sometimes without close supervision', selfRating: '', managerRating: '' },
        ],
      },
    ],
    performanceKeyFactorsManagerComments: '',

    // Section B: BSC Reference (auto-populated from BSC for the same quarter)
    bscScore: undefined,
    bscQuarter: undefined,
    bscManagerComments: '',

    // Section C: KPI Setting
    nextQuarterKPIs: [],

    // Section D: OKRs
    okrObjective: '',
    okrObjectiveChallenge: undefined,
    okrKeyResults: [],

    // Section E: CIS
    cisImprovements: [],
    cisManagerComments: '',

    // Section F: 360°
    feedback360: '',

    // Section G: Core Values (ACT + REF)
    coreValues: [
      // ACT - Accountable, Community, Transparent
      { value: 'ACCOUNTABLE', acronym: 'A', description: 'Accountable to all stakeholders by empowering the team to deliver the best development and investment opportunities', selfRating: '', managerRating: '', group: 'ACT' },
      { value: 'COMMUNITY', acronym: 'C', description: 'Working as one body to better serve the community and enable a sustainable environment for clients to live in WestProp lifestyle estates', selfRating: '', managerRating: '', group: 'ACT' },
      { value: 'TRANSPARENT', acronym: 'T', description: 'Trusted and transparent by being honest and ethical at all times', selfRating: '', managerRating: '', group: 'ACT' },
      // REF - Reputable, Execution, Flexible
      { value: 'REPUTABLE', acronym: 'R', description: 'Reputable, respected, responsible, and reliable as an individual and a company', selfRating: '', managerRating: '', group: 'REF' },
      { value: 'EXECUTION', acronym: 'E', description: 'Excellence in execution, being efficient with continued education, and being a shining example', selfRating: '', managerRating: '', group: 'REF' },
      { value: 'FLEXIBLE', acronym: 'F', description: "Focus on clients' needs whilst being flexible and innovative to set market trends", selfRating: '', managerRating: '', group: 'REF' },
    ],

    // Section H: Personal Development
    strengths: [],
    developmentNeeds: [],
    managerDevelopmentSupport: '',

    // Final Section
    overallManagerRating: undefined,
    overallManagerComments: '',
  } as PEARReviewFormType,
};

export type { PEARReviewFormType as pearReviewType };
export default pearReviewModel;
